import { useCallback, useState } from 'react'
import { PermissionButton } from '../components/Permissions.js'
import { useApi, useAsync } from '../lib/useApi.js'
import { ROLES, type Invitation, type Role } from '../lib/api.js'
import { CopyButton, ErrorBox, Empty, Loading, relative, until } from '../components/ui.js'

export default function Members() {
  const api = useApi()
  const [reloadKey, setReloadKey] = useState(0)
  const reload = useCallback(() => setReloadKey((n) => n + 1), [])

  const members = useAsync(() => api.listMembers(), [api, reloadKey])
  const invitations = useAsync(() => api.listInvitations(), [api, reloadKey])

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Members</h1>
          <p className="sub">
            Everyone in this organization and what they may do. Membership and roles live in NimblyBase -
            Clerk only signs people in.
          </p>
        </div>
      </div>

      <InviteForm onInvited={reload} />

      <h2>In this organization</h2>
      <div className="card">
        {members.loading ? (
          <Loading />
        ) : members.error ? (
          <ErrorBox retry={members.reload}>{members.error}</ErrorBox>
        ) : !members.data?.members.length ? (
          <Empty>No members found.</Empty>
        ) : (
          members.data.members.map((m) => (
            <div className="row" key={m.userId}>
              <div className="row-main">
                <div className="row-title">{m.name || m.email}</div>
                <div className="row-sub">
                  {m.email} · joined {relative(m.joinedAt)}
                </div>
              </div>
              <span className="pill">{m.role}</span>
            </div>
          ))
        )}
      </div>

      <Invitations
        loading={invitations.loading}
        error={invitations.error}
        invitations={invitations.data?.invitations ?? []}
        onRevoked={reload}
      />
    </>
  )
}

function InviteForm({ onInvited }: { onInvited: () => void }) {
  const api = useApi()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<Role>('developer')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [issued, setIssued] = useState<{ email: string; token: string } | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const result = await api.createInvitation({ email: email.trim(), role })
      setIssued({ email: result.invitation.email, token: result.token })
      setEmail('')
      onInvited()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send that invitation.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <h2>Invite someone</h2>

      {issued && (
        <div className="notice">
          <strong>Invitation link for {issued.email} — copy it now, it will not be shown again.</strong>
          <div className="secret">{issued.token}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <CopyButton value={issued.token} />
            <button className="btn" onClick={() => setIssued(null)}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      <form className="card" onSubmit={submit} style={{ padding: 16, marginBottom: 16 }}>
        {error && <ErrorBox>{error}</ErrorBox>}
        <div className="form-row">
          <div className="field" style={{ flex: 1, marginBottom: 0 }}>
            <label htmlFor="i-email">Email address</label>
            <input
              id="i-email"
              type="email"
              value={email}
              required
              maxLength={254}
              placeholder="colleague@company.com"
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="field" style={{ width: 200, marginBottom: 0 }}>
            <label htmlFor="i-role">Role</label>
            <select id="i-role" value={role} onChange={(e) => setRole(e.target.value as Role)}>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <PermissionButton
            permission="member:invite"
            className="btn btn-primary"
            disabled={busy || !email.trim()}
          >
            {busy ? 'Sending…' : 'Send invitation'}
          </PermissionButton>
        </div>
        <p className="sub small" style={{ marginTop: 10 }}>
          You cannot invite someone to a role above your own. The link is shown once and expires in seven
          days.
        </p>
      </form>
    </>
  )
}

function Invitations({
  invitations,
  loading,
  error,
  onRevoked,
}: {
  invitations: Invitation[]
  loading: boolean
  error: string | null
  onRevoked: () => void
}) {
  const api = useApi()
  const [revoking, setRevoking] = useState<string | null>(null)
  const [revokeError, setRevokeError] = useState<string | null>(null)

  // Accepted invitations are history — that person now shows up above as a
  // member. Revoked and expired ones stay visible, because "why did they
  // never get in?" is the question this list has to answer.
  const open = invitations.filter((i) => i.state !== 'accepted')

  async function revoke(id: string) {
    setRevoking(id)
    setRevokeError(null)
    try {
      await api.revokeInvitation(id)
      onRevoked()
    } catch (err) {
      setRevokeError(err instanceof Error ? err.message : 'Could not revoke that invitation.')
    } finally {
      setRevoking(null)
    }
  }

  return (
    <>
      <h2>Invitations</h2>
      {revokeError && <ErrorBox>{revokeError}</ErrorBox>}
      <div className="card">
        {loading ? (
          <Loading />
        ) : error ? (
          <ErrorBox>{error}</ErrorBox>
        ) : !open.length ? (
          <Empty>No open invitations.</Empty>
        ) : (
          open.map((i) => (
            <div className="row" key={i.id}>
              <div className="row-main">
                <div className="row-title">{i.email}</div>
                <div className="row-sub">
                  {i.role}
                  {i.state === 'pending' && <> · expires {until(i.expiresAt)}</>}
                </div>
              </div>
              <span className={`pill ${pillClass(i.state)}`}>{i.state}</span>
              {i.state === 'pending' && (
                <PermissionButton permission="member:invite" className="btn btn-danger" disabled={revoking !== null} onClick={() => revoke(i.id)}>
                  {revoking === i.id ? 'Revoking…' : 'Revoke'}
                </PermissionButton>
              )}
            </div>
          ))
        )}
      </div>
    </>
  )
}

function pillClass(state: Invitation['state']): string {
  if (state === 'pending') return 'pill-warn'
  if (state === 'revoked') return 'pill-dead'
  return 'pill-dead'
}
